/** Costumes Suite. Character-by-character mood boards + measurement
 *  matrices + continuity logs. */
import { useState } from "react";
import { Plus, Trash2, Shirt, Ruler, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import useCubicleRecords from "./useCubicleRecords";

const MEASUREMENTS = ["chest", "waist", "hip", "inseam", "shoulder", "shirt", "shoe"];

export default function CostumesCubicle({ projectId }) {
  const characters = useCubicleRecords(projectId, "costumes", "costume_character");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({
    character: "", actor: "", continuity_notes: "",
    mood_board_urls: "",
    measurements: Object.fromEntries(MEASUREMENTS.map((m) => [m, ""])),
  });

  return (
    <div className="space-y-6" data-testid="costumes-cubicle">
      <section className="cs-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              Character Wardrobe Boards
            </div>
            <div className="font-display text-lg uppercase tracking-tight">
              {characters.items.length} character(s)
            </div>
          </div>
          <Button onClick={() => setDialog(true)}
                  className="h-8 gap-1 bg-white text-black hover:bg-white/90"
                  data-testid="costume-add">
            <Plus size={13}/> Character
          </Button>
        </div>

        {characters.items.length === 0 ? (
          <div className="text-sm text-white/45 py-8 text-center">
            No character boards yet. Every look, size, and continuity
            note ties back to a character card here.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2" data-testid="costume-list">
            {characters.items.map((r) => <CostumeCard key={r.id} record={r}
                                                       onPatch={characters.patch}
                                                       onRemove={characters.remove}/>)}
          </div>
        )}
      </section>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl bg-neutral-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-tight">Add Character</DialogTitle>
            <DialogDescription className="sr-only">Costume character board entry.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Character</Label>
              <Input value={form.character}
                     onChange={(e) => setForm({ ...form, character: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="costume-character-input"/></div>
            <div><Label>Actor</Label>
              <Input value={form.actor}
                     onChange={(e) => setForm({ ...form, actor: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"/></div>
            <div className="col-span-2"><Label>Mood board image URLs (comma-separated)</Label>
              <Textarea rows={2} value={form.mood_board_urls}
                        onChange={(e) => setForm({ ...form, mood_board_urls: e.target.value })}
                        className="mt-1 bg-black/40 border-white/15"
                        placeholder="https://…/look1.jpg, https://…/look2.jpg"/></div>
            <div className="col-span-2">
              <Label>Measurements</Label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1">
                {MEASUREMENTS.map((m) => (
                  <div key={m}>
                    <Input placeholder={m}
                           value={form.measurements[m]}
                           onChange={(e) => setForm({ ...form,
                             measurements: { ...form.measurements, [m]: e.target.value } })}
                           className="h-9 bg-black/40 border-white/15 text-xs"/>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-2"><Label>Continuity Notes</Label>
              <Textarea value={form.continuity_notes}
                        onChange={(e) => setForm({ ...form, continuity_notes: e.target.value })}
                        className="mt-1 bg-black/40 border-white/15"/></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={async () => {
              if (!form.character.trim()) return;
              await characters.create("costume_character", {
                ...form,
                mood_board_urls: form.mood_board_urls.split(",").map((s) => s.trim()).filter(Boolean),
              });
              setDialog(false);
              setForm({ character: "", actor: "", continuity_notes: "",
                        mood_board_urls: "",
                        measurements: Object.fromEntries(MEASUREMENTS.map((m) => [m, ""])) });
            }} className="bg-white text-black hover:bg-white/90"
               data-testid="costume-submit">Save character</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function CostumeCard({ record, onPatch, onRemove }) {
  const d = record.data || {};
  const boards = d.mood_board_urls || [];
  const meas = d.measurements || {};
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-4"
         data-testid={`costume-card-${record.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-white">
            {d.character}{d.actor && <span className="ml-2 text-white/55 text-xs">as {d.actor}</span>}
          </div>
        </div>
        <Button variant="ghost" onClick={() => onRemove(record.id)}
                className="h-7 px-2 text-white/45 hover:text-rose-300"
                data-testid={`costume-remove-${record.id}`}>
          <Trash2 size={13}/>
        </Button>
      </div>

      {boards.length > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {boards.map((u, i) => (
            <img key={i} src={u} alt=""
                 className="h-24 w-24 rounded-lg object-cover border border-white/10 shrink-0"
                 onError={(e) => { e.currentTarget.style.display = "none"; }}/>
          ))}
        </div>
      )}

      {Object.values(meas).some(Boolean) && (
        <div className="mt-3">
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/55 inline-flex items-center gap-1">
            <Ruler size={10}/> Measurements
          </div>
          <div className="grid grid-cols-3 gap-x-3 gap-y-1 mt-1 text-[11px] text-white/70">
            {MEASUREMENTS.filter((m) => meas[m]).map((m) => (
              <div key={m}><span className="text-white/45">{m}: </span>{meas[m]}</div>
            ))}
          </div>
        </div>
      )}

      {d.continuity_notes && (
        <div className="mt-3">
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/55 inline-flex items-center gap-1">
            <ClipboardList size={10}/> Continuity
          </div>
          <div className="text-xs text-white/70 mt-0.5 whitespace-pre-wrap">{d.continuity_notes}</div>
        </div>
      )}
    </div>
  );
}
